function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

import _ from 'underscore';
import URI from 'urijs';
import Backbone from '@polleverywhere/backbone';
import Base from './base';
import SubModel from './mixins/_sub_model';
import Live from './mixins/_live';
import Instance from './mixins/_instance';
import env from '../env.js';
import { cleanString, deepDefaults, pluralize } from '../util';
import FlashOptions from './flash_options';
import Results from './results'; // Dynamically import the following modules asynchronously to prevent webpack
// circular dependency errors at build time.

var Competition;
var Survey;
import('./competition').then(function (module) {
  Competition = module["default"];
});
import('./survey').then(function (module) {
  Survey = module["default"];
});
/**
 * Parse collection instance data into an MQA model. If model already exists, it
 * is updated. If not, one is created.
 * @param {Object} attrs Flat data obj, typically from API or Firehose
 * @param {string} type Collection type, e.g survey|competition
 * @return {Object} Return the same data obj passed in
 */

function parseCollectionInstance(attrs, type) {
  if (attrs === void 0) {
    attrs = {};
  }

  var MODEL_CLASSES = {
    competition: Competition,
    survey: Survey
  };
  var instanceKey = type + "_instance";
  var instanceData = attrs[instanceKey]; // Store model into a private member property named with this key.

  var privateKey = "_" + instanceKey;

  if (instanceData) {
    if (this[type]) {
      this[type].set(this[type].parse(instanceData));
    } else {
      this[privateKey] = new MODEL_CLASSES[type](instanceData);
    }
  } else if (instanceData === null) {
    delete this[privateKey];
  } // TODO: When all consumers are no longer referencing the `*_instance`
  // attr directly, uncomment this to remove the key from the top model and
  // maintain a single source of truth for the MQA.
  // delete attrs[instanceKey]


  return attrs;
}
/**
 * Base Poll model that other types inherit from. All *Url methods are relative
 * URLs. This conforms to how Backbone treats Url methods.
 *
 * @event change:flash_options:<name> Flash options change events
 *   @param [FlashOptions] model
 *   @param [Object] value
 */


var Poll = /*#__PURE__*/function (_Base) {
  _inheritsLoose(Poll, _Base);

  function Poll() {
    return _Base.apply(this, arguments) || this;
  }

  var _proto = Poll.prototype;

  _proto.initialize = function initialize(attrs, options) {
    if (attrs === void 0) {
      attrs = {};
    }

    if (options === void 0) {
      options = {};
    }

    _Base.prototype.initialize.apply(this, arguments);

    this.include(Live);
    this.include(SubModel);
    this.include(Instance);
    this.initSubModel('type_options');
    this.initSubModel('presenter');
    this.initSubModel('flash_options', {
      model: FlashOptions,
      prefix: '_'
    }).poll = this; // Always run attrs thru activity parsers so they can be normalized. This
    // is important so that instance attributes remain backwards compatible.

    this.parse(attrs, options);
  }
  /**
   * Returns a read-only competition model that this poll belongs to.
   * @return {Competition}
   */
  ;

  _proto.fetch = function fetch(options) {
    if (options === void 0) {
      options = {};
    } // Clone options to prevent mutating the options argument passed in.


    options = Object.assign({}, options);
    options.data = Object.assign({}, options.data); // Ensure collection data is always included in the response. This ensures
    // SQAs are aware of whether they belong to an MQA.

    var includes = options.data.include ? options.data.include.split(',') : [];

    if (!includes.includes('collection')) {
      includes.push('collection');
      options.data.include = includes.join(',');
    }

    return _Base.prototype.fetch.call(this, options);
  };

  _proto.liveChannel = function liveChannel() {
    var type = pluralize(2, this.type());
    return type + "/" + this.get('permalink') + "/firehose.json";
  }
  /**
   * Parse Firehose response into the model.
   * @param {object} data Firehose response data.
   * @return {object} Returns potentially mutated data.
   */
  ;

  _proto.processLiveResponse = function processLiveResponse(data) {
    if (data === void 0) {
      data = {};
    } // Update the poll position to trigger poll reordering


    var newPosition = 'position' in data ? data.position : null;

    if (newPosition && newPosition !== this.get('position')) {
      data.position = newPosition;
    } // Always update the activity for every Firehose update.


    this.set(this.parse(data));
    return data;
  } // these are overridden by MCPs
  ;

  _proto.isGraded = function isGraded() {
    return false;
  };

  _proto.hasAlphaNumericKeyword = function hasAlphaNumericKeyword() {
    return false;
  };

  _proto.hasCustomGlobalKeyword = function hasCustomGlobalKeyword() {
    return false;
  } // this is overridden by FTPs
  ;

  _proto.supportsModeration = function supportsModeration() {
    return false;
  };

  _proto.flashOptions = function flashOptions() {
    return this._flashOptions;
  };

  _proto.type = function type(_type) {
    if (_type) {
      return _type;
    } else if (this.has('type')) {
      return this.get('type');
    } else {
      return this.defaultType;
    }
  };

  _proto.remainingVotes = function remainingVotes(responseCount) {
    return Math.max((this.get('max_votes') || Infinity) - responseCount, 0);
  };

  _proto.percentageVotesUsed = function percentageVotesUsed(remainingVotes) {
    return remainingVotes / this.get('max_votes') * 100;
  };

  _proto.permalink = function permalink() {
    return this.get('permalink');
  };

  _proto.start = function start(opts) {
    return this.toggle('state', opts, 'opened');
  };

  _proto.stop = function stop(opts) {
    return this.toggle('state', opts, 'closed');
  };

  _proto.toggleState = function toggleState(opts) {
    var newState = this.get('state') === 'opened' ? 'closed' : 'opened';
    return this.toggle('state', opts, newState);
  };

  _proto.toggleSmsEnabled = function toggleSmsEnabled(opts) {
    return this.toggle('sms_enabled', opts);
  };

  _proto.toggleWebEnabled = function toggleWebEnabled(opts) {
    return this.toggle('web_enabled', opts);
  };

  _proto.toggleRegisteredParticipantsOnly = function toggleRegisteredParticipantsOnly(val, opts) {
    if (opts === void 0) {
      opts = {};
    }

    return this.toggle('registered_participants_only', opts, val);
  };

  _proto.toggleEditableAnswers = function toggleEditableAnswers(val, opts) {
    if (opts === void 0) {
      opts = {};
    }

    return this.toggle('editable_answers', opts, val);
  };

  _proto.toggleMaxVotes = function toggleMaxVotes(maxVotesSelect, maxVotes, opts) {
    if (opts === void 0) {
      opts = {};
    }

    if (maxVotesSelect === 'unlimited') {
      maxVotes = null;
    }

    return this.toggle('max_votes', opts, maxVotes);
  };

  _proto.toggleAnonymous = function toggleAnonymous(val, opts) {
    if (opts === void 0) {
      opts = {};
    }

    return this.toggle('anonymous', opts, val);
  };

  _proto.toggle = function toggle(attr, opts, val) {
    if (opts === void 0) {
      opts = {};
    }

    var attrs = {};
    attrs[attr] = typeof val !== 'undefined' ? val : !this.get(attr); // Allow defaults for nested headers object

    deepDefaults(opts, {
      patch: true,
      headers: {
        Prefer: 'return=minimal'
      }
    });
    return this.save(attrs, opts);
  };

  _proto.isActivated = function isActivated() {
    return this.get('current');
  };

  _proto.isStarted = function isStarted() {
    return this.get('state') === 'opened';
  };

  _proto.isStopped = function isStopped() {
    return this.get('state') === 'closed';
  };

  _proto.isMaxedOut = function isMaxedOut() {
    return this.get('state') === 'maxed_out';
  };

  _proto.isWebEnabled = function isWebEnabled() {
    return this.get('web_enabled');
  };

  _proto.isWebRespondable = function isWebRespondable() {
    return this.isStarted() && this.isWebEnabled();
  }
  /**
   * Figure out if this is web respondable for a participant. We need this so we
   * can calculate if the participant has voted more than once, depending on the
   * value of max_votes.
   */
  ;

  _proto.isWebRespondableFor = function isWebRespondableFor(participant) {
    return !this.hasMaximumResponses(participant) && this.isWebRespondable();
  };

  _proto.isGlobalKeywordsEnabled = function isGlobalKeywordsEnabled() {
    return this.get('sms_enabled') && !this.get('simple_keywords');
  };

  _proto.hasMaximumResponses = function hasMaximumResponses(participant) {
    // TODO: resultsForPoll doesn't exist in newer Models.
    if (this.get('max_votes')) {
      return (typeof participant.resultsForPoll === 'function' ? participant.resultsForPoll(this, false).size() : undefined) >= this.get('max_votes');
    } else {
      return false; // A max votes threshold wasn't set so assume unlimited
    }
  };

  _proto.url = function url() {
    return this.instance.url();
  }
  /**
   * Generates url to old API endpoints (non-instance) via permalink.
   */
  ;

  _proto._urlBase = function _urlBase() {
    var type = pluralize(2, this.type());
    var permalink = this.attributes.permalink;
    return URI('/').segment(type).segment(permalink);
  }
  /**
   * Public shareable response link. Should support both cases when poll has
   * only the permalink or a participant_key.
   * @return {string}
   */
  ;

  _proto.webVotingUrl = function webVotingUrl() {
    return this.instance.webRespondUrl;
  }
  /*
   * A URL a user would visit in a browser, as opposed to @url which is just
   * a JSON endpoint.
   */
  ;

  _proto.showUrl = function showUrl() {
    return this._urlBase().toString();
  };

  _proto.editUrl = function editUrl() {
    return this._urlBase().segment('edit').toString();
  };

  _proto.duplicateUrl = function duplicateUrl() {
    return this._urlBase().segment('duplicate').toString();
  };

  _proto.undo = function undo() {
    return Backbone.ajax({
      url: this.undoUrl(),
      type: 'delete'
    });
  };

  _proto.undoUrl = function undoUrl() {
    return URI('/api/polls').segment(this.id.toString()).segment('results').toString();
  };

  _proto.responseHistoryUrl = function responseHistoryUrl() {
    return this._urlBase().segment('results').toString();
  };

  _proto.sendToListUrl = function sendToListUrl() {
    return this._urlBase().segment('send_to_list').toString();
  };

  _proto.resultsUrl = function resultsUrl() {
    var link = this.get('permalink');
    return URI('/polls').segment(link).segment('results').toString();
  };

  _proto.screenshotUrl = function screenshotUrl(opts) {
    if (opts === void 0) {
      opts = {};
    } // backwards compatible with screenshotUrl(true)


    if (opts === true) {
      opts = {
        attachment: true
      };
    }

    return this._urlBase().absoluteTo(env.get('poll_renderer_url')).query(opts).toString();
  }
  /**
   * Generate a rootless JSON object of all model attrs.
   * @param {Object} options
   * @return {Object}
   */
  ;

  _proto.toJSON = function toJSON() {
    var base = {
      type: this.type()
    };
    return Object.assign(base, this.pick(this.modelProperties));
  };

  _proto.shouldHideKeywords = function shouldHideKeywords() {
    var smsEnabled = this.attributes.sms_enabled;
    var webEnabled = this.attributes.web_enabled;
    var current = this.attributes.current;
    var simpleKeywords = this.attributes.simple_keywords;
    var webAddress = this.attributes.web_address;
    var state = this.attributes.state;
    var isClosed = state === 'closed';
    var votingDisabled = !smsEnabled && !webEnabled;
    var slashVoteOnly = !smsEnabled && webEnabled && webAddress !== null;
    var shortKeywordsAndNotCurrent = !current && simpleKeywords;
    var hideKeywords = !this.flashOptions().get('showKeywords');
    var viewingArchivedRun = this.flashOptions().has('teamPollArchiveId') && this.flashOptions().get('teamPollArchiveId') !== 0;
    return isClosed || votingDisabled || slashVoteOnly || shortKeywordsAndNotCurrent || hideKeywords || viewingArchivedRun;
  };

  _proto.cleanTitle = function cleanTitle(opts) {
    if (opts === void 0) {
      opts = {};
    }

    var title = this.get('title');
    return cleanString(title, opts);
  };

  _proto.refreshArchives = function refreshArchives() {
    var _this = this;

    if (this.archives.models.length) {
      _.each(this.archives.pluck('id'), function (id) {
        _this.archives.remove(_this.archives.get(id));
      }, this);
    }

    return this.archives.fetch();
  };

  _proto.parse = function parse(json) {
    if (json === void 0) {
      json = {};
    }

    if (this.instance) {
      // All instance data must be normalized to support legacy keys.
      json = this.instance.normalizeActivityData(json);
    } // Parse collection instance data if they exist.


    parseCollectionInstance.call(this, json, 'competition');
    parseCollectionInstance.call(this, json, 'survey');
    return json;
  };

  _proto.requiresRegistration = function requiresRegistration() {
    return !!this.get('registered_participants_only');
  } // TODO: Move to current poll module
  ;

  _proto.onScreenUrl = function onScreenUrl() {
    return '/api/activation';
  }
  /*
   * Deprecated
   * TODO switch mobile over to using #push.
   * Viz is already using #push / #unpush
   */
  ;

  _proto.setAsCurrent = function setAsCurrent(opts) {
    if (opts === void 0) {
      opts = {};
    }

    opts = _.defaults(opts, {
      url: this.onScreenUrl()
    });
    opts.type = 'PUT';
    opts.data = {
      type: 'poll',
      uid: this.get('permalink')
    };
    opts.processData = true;
    return this.sync('create', this, opts);
  };

  _proto.toggleSimpleKeywords = function toggleSimpleKeywords(value) {
    return this.toggle('simple_keywords');
  };

  _proto.activate = function activate() {
    var _this2 = this;

    var opts = {
      type: 'PUT',
      url: this.onScreenUrl(),
      data: {
        type: this.type(),
        uid: this.get('permalink')
      },
      processData: true
    }; // TODO find out why .then() doesn't work properly here in specs.

    return this.sync('create', this, opts).done(function () {
      _this2.presenter.set({
        active: true
      });

      _this2.set({
        current: true
      });
    });
  };

  _proto.deactivate = function deactivate() {
    var _this3 = this;

    var data = {};

    if (this.presenter) {
      data.user_id = this.presenter.get('id');
    } else {
      data.user_id = this.get('owner_id');
    } // TODO find out why .then() doesn't work properly here in specs.


    return this.sync('delete', this, {
      url: this.onScreenUrl(),
      processData: true,
      data: data
    }).done(function () {
      _this3.presenter.set({
        active: false
      });

      _this3.set({
        current: false
      });
    });
  };

  _proto.getTypeOption = function getTypeOption(name) {
    return this.typeOptions.get(name);
  };

  _proto.setTypeOption = function setTypeOption(name, value) {
    this.typeOptions.set(name, value);
  };

  _proto.unsetTypeOption = function unsetTypeOption(name) {
    this.typeOptions.unset(name);
  };

  _proto.isSimpleKeywords = function isSimpleKeywords() {
    return this.get('simple_keywords');
  };

  Poll.fetchByPermalink = function fetchByPermalink(permalink) {
    return Backbone.ajax({
      url: URI('/api/polls').segment(permalink).toString(),
      method: 'GET'
    });
  };

  _proto.isMQA = function isMQA() {
    return false;
  };

  _proto.mqaType = function mqaType() {
    if (this.has('competition_id')) {
      return 'competition';
    } else if (this.has('survey_id')) {
      return 'survey';
    }
  };

  _createClass(Poll, [{
    key: "defaults",
    get: function get() {
      return {
        web_enabled: true,
        web_address: 'PollEv.com'
      };
    } // These model properties apply to all poll types.
    // If you have model type specific properties, add them
    // to their cooresponding model

  }, {
    key: "modelProperties",
    get: function get() {
      return ['activated', 'anonymous', 'competition_id', 'confirmation_type', 'custom_confirmation_message', 'display_state', 'editable_answers', 'flash_options', 'group_id', 'max_votes', 'owner_id', 'participant_key', 'permalink', 'position', 'presenter_key', 'registered_participants_only', 'shared_key', 'simple_keywords', 'sms_enabled', 'start_at', 'state', 'stop_at', 'survey_id', 'title', 'title_hidden', 'type_options', 'web_enabled'];
    }
    /**
     * Read-only property consumers can use to determine if this activity is
     * loaded with an archived result. Returns `null` when using latest.
     * @return {Archive}
     */

  }, {
    key: "currentArchive",
    get: function get() {
      return this.instance.currentArchive;
    }
    /**
     * Collection of participant submitted results.
     * @return {Results}
     */

  }, {
    key: "results",
    get: function get() {
      if (!this._results) {
        this._results = new Results(null, {
          poll: this
        });
      }

      return this._results;
    }
  }, {
    key: "competition",
    get: function get() {
      if (this.collection && this.collection.competition) {
        return this.collection.competition;
      } else {
        return this._competition_instance;
      }
    }
    /**
     * Returns a read-only survey model that this poll belongs to.
     * @return {Survey}
     */

  }, {
    key: "survey",
    get: function get() {
      if (this.collection && this.collection.poll) {
        return this.collection.poll;
      } else {
        return this._survey_instance;
      }
    }
  }]);

  return Poll;
}(Base);

export { Poll as default };